/*
 * Weltkarte — Punkte als Tabs, Karte mit Zoom und Pan.
 *
 * Vorher steckte diese Logik als 300 Zeilen inline im Section-Markup: nicht
 * cachebar und pro Section erneut im HTML. Jetzt ein Asset mit defer, das sich
 * je Section-Wurzel selbst initialisiert.
 *
 * Drei Regeln, die den Umbau bestimmt haben:
 *   1. Die Karte darf das Scrollen der Seite nicht kapern. Ein Finger pannt
 *      erst, wenn hineingezoomt wurde; das Rad zoomt nur mit Ctrl/Cmd.
 *   2. Die Punkte funktionieren auch ohne Zoom. Vorher lag ein early return
 *      vor der Klick-Registrierung — fehlte ein Zoom-Element, waren alle
 *      Punkte tot, waehrend die Rotation weiterlief.
 *   3. Wer liest, wird nicht weggerissen: die Rotation haelt bei Hover, Fokus,
 *      im Hintergrund und ausserhalb des Viewports an — und laeuft gar nicht,
 *      wenn reduzierte Bewegung gewuenscht ist.
 */
(() => {
  const ROTATE_MS = 5000;
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const narrowViewport = window.matchMedia('(max-width: 989px)');

  const initWeltkarte = (root) => {
    if (root.dataset.weltkarteReady === 'true') return;
    root.dataset.weltkarteReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-weltkarte-point]'));
    const panels = Array.from(root.querySelectorAll('[data-weltkarte-panel]'));
    if (tabs.length === 0 || panels.length === 0) return;

    /* ---------------------------------------------------------------- Tabs */

    const setActive = (id) => {
      let activeTab = null;

      tabs.forEach((tab) => {
        const isActive = tab.dataset.weltkartePoint === id;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        // Roving tabindex: nur der aktive Punkt liegt im Tab-Fluss, innerhalb
        // der Gruppe navigiert man mit den Pfeiltasten.
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive) activeTab = tab;
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.weltkartePanel === id;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      return activeTab;
    };

    const activeIndex = () => tabs.findIndex((tab) => tab.classList.contains('is-active'));

    const activate = (index, { focus = false } = {}) => {
      const tab = tabs[(index + tabs.length) % tabs.length];
      const activeTab = setActive(tab.dataset.weltkartePoint);
      if (focus && activeTab) activeTab.focus();
      return activeTab;
    };

    /* ---------------------------------------------------------------- Zoom */

    const stage = root.querySelector('[data-weltkarte-zoom-stage]');
    const surface = root.querySelector('[data-weltkarte-zoom-surface]');
    const hasZoom = Boolean(stage && surface);

    let zoomLevel = 1;
    let translateX = 0;
    let translateY = 0;

    const clampPan = () => {
      const scaledWidth = stage.scrollWidth * zoomLevel;
      const scaledHeight = stage.scrollHeight * zoomLevel;
      const maxOffsetX = Math.max(0, (scaledWidth - surface.clientWidth) / 2);
      const maxOffsetY = Math.max(0, (scaledHeight - surface.clientHeight) / 2);
      translateX = Math.min(maxOffsetX, Math.max(-maxOffsetX, translateX));
      translateY = Math.min(maxOffsetY, Math.max(-maxOffsetY, translateY));
    };

    const updateZoom = () => {
      if (!hasZoom) return;
      clampPan();
      stage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoomLevel})`;
      surface.classList.toggle('is-zoomed', zoomLevel > MIN_ZOOM);
    };

    const setZoom = (nextZoom) => {
      if (!hasZoom) return;
      const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
      if (clamped === zoomLevel) return;

      const ratio = clamped / zoomLevel;
      translateX *= ratio;
      translateY *= ratio;
      zoomLevel = clamped;
      updateZoom();
    };

    const resetZoom = () => {
      zoomLevel = MIN_ZOOM;
      translateX = 0;
      translateY = 0;
      updateZoom();
    };

    // Vorher wurde dieser Wert einmal beim Laden aus matchMedia gelesen und nie
    // aktualisiert — nach einem Resize oder einer Drehung stimmte er nicht mehr.
    const focusZoom = () => (narrowViewport.matches ? 2.2 : 1.8);

    const zoomToPoint = (tab) => {
      if (!hasZoom || !tab) return;

      zoomLevel = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, focusZoom()));
      updateZoom();

      const pointRect = tab.getBoundingClientRect();
      const surfaceRect = surface.getBoundingClientRect();
      translateX += surfaceRect.left + surfaceRect.width / 2 - (pointRect.left + pointRect.width / 2);
      translateY += surfaceRect.top + surfaceRect.height / 2 - (pointRect.top + pointRect.height / 2);
      updateZoom();
    };

    /* ------------------------------------------------------------ Rotation */

    let rotateTimer = null;
    let paused = false;

    const rotationAllowed = () => !reducedMotion.matches && tabs.length > 1;

    const stopRotate = () => {
      if (rotateTimer !== null) window.clearInterval(rotateTimer);
      rotateTimer = null;
    };

    const startRotate = () => {
      stopRotate();
      if (paused || !rotationAllowed()) return;
      rotateTimer = window.setInterval(() => {
        const tab = activate(activeIndex() + 1);
        zoomToPoint(tab);
      }, ROTATE_MS);
    };

    const pause = () => {
      paused = true;
      stopRotate();
    };

    const resume = () => {
      paused = false;
      startRotate();
    };

    // Lesen schlaegt Rotieren: Zeigen, Tastaturfokus und Beruehrung halten an.
    root.addEventListener('pointerenter', pause);
    root.addEventListener('pointerleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) resume();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopRotate();
      else startRotate();
    });
    reducedMotion.addEventListener('change', startRotate);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? startRotate() : stopRotate()));
        },
        { threshold: 0.2 }
      ).observe(root);
    } else {
      startRotate();
    }

    /* ------------------------------------------------- Bedienung der Tabs */
    /* Bewusst vor dem Zoom-Setup: die Punkte muessen auch dann schalten,
       wenn Stage oder Surface fehlen. */

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        zoomToPoint(activate(index));
        startRotate();
      });

      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        let target = null;

        if (step) target = activeIndex() + step;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;

        event.preventDefault();
        zoomToPoint(activate(target, { focus: true }));
      });
    });

    const initial = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    setActive(initial.dataset.weltkartePoint);

    if (!hasZoom) return;

    /* ------------------------------------------------ Zoom-Bedienelemente */

    root.querySelector('[data-weltkarte-zoom-in]')?.addEventListener('click', () => setZoom(zoomLevel + 0.2));
    root.querySelector('[data-weltkarte-zoom-out]')?.addEventListener('click', () => setZoom(zoomLevel - 0.2));
    root.querySelector('[data-weltkarte-zoom-reset]')?.addEventListener('click', resetZoom);

    stage.addEventListener('dragstart', (event) => event.preventDefault());

    // Nur mit Ctrl/Cmd zoomen. Vorher schluckte die Karte jedes Rad-Event und
    // die Seite liess sich an dieser Stelle nicht mehr scrollen.
    surface.addEventListener(
      'wheel',
      (event) => {
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        setZoom(zoomLevel + (event.deltaY > 0 ? -0.1 : 0.1));
      },
      { passive: false }
    );

    /* ------------------------------------------------------- Pan und Pinch */

    const pointers = new Map();
    let isDragging = false;
    let isPinching = false;
    let startX = 0;
    let startY = 0;
    let panStartX = 0;
    let panStartY = 0;
    let pinchStartDistance = 0;
    let pinchStartZoom = zoomLevel;
    let pinchStartTranslateX = 0;
    let pinchStartTranslateY = 0;
    let pinchCenterX = 0;
    let pinchCenterY = 0;

    const pinchDistance = () => {
      const [a, b] = Array.from(pointers.values());
      return a && b ? Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) : 0;
    };

    const pinchCenter = () => {
      const [a, b] = Array.from(pointers.values());
      if (!a || !b) return null;
      return { clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 };
    };

    const startPinch = () => {
      const center = pinchCenter();
      pinchStartDistance = pinchDistance();
      if (!center || pinchStartDistance === 0) return;

      isPinching = true;
      isDragging = false;
      surface.classList.add('is-dragging');
      pinchStartZoom = zoomLevel;
      pinchStartTranslateX = translateX;
      pinchStartTranslateY = translateY;
      pinchCenterX = center.clientX;
      pinchCenterY = center.clientY;
    };

    const beginDrag = (clientX, clientY) => {
      isDragging = true;
      surface.classList.add('is-dragging');
      startX = clientX;
      startY = clientY;
      panStartX = translateX;
      panStartY = translateY;
    };

    surface.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' && event.button !== 0) return;
      if (event.target.closest('.weltkarte__point')) return;

      pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
      // Wirft bei unbekannter Pointer-ID (NotFoundError). Ungefangen stirbt
      // daran das gesamte Ziehen, obwohl Capture nur Komfort ist.
      try {
        surface.setPointerCapture(event.pointerId);
      } catch (error) {
        /* ohne Capture weiterarbeiten */
      }

      if (pointers.size === 2) {
        startPinch();
        return;
      }
      if (pointers.size > 1) return;

      /* Ein Finger gehoert immer dem Seitenscroll — verschoben wird die Karte
         auf dem Handy mit zwei Fingern. Der Versuch, das an den Zoomgrad zu
         haengen, ging schief: der Auto-Zoom beim Laden steht sofort auf 2.2,
         damit war die Geste wieder gesperrt und die Seite an dieser Stelle
         nicht scrollbar. Mit der Maus bleibt Ziehen unveraendert moeglich. */
      if (event.pointerType === 'touch') return;

      beginDrag(event.clientX, event.clientY);
    });

    surface.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });

      if (isPinching && pointers.size >= 2) {
        const distance = pinchDistance();
        if (pinchStartDistance === 0 || distance === 0) return;

        const center = pinchCenter();
        const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, pinchStartZoom * (distance / pinchStartDistance)));
        const ratio = nextZoom / pinchStartZoom;

        if (center) {
          translateX = pinchStartTranslateX + (center.clientX - pinchCenterX);
          translateY = pinchStartTranslateY + (center.clientY - pinchCenterY);
        }

        translateX *= ratio;
        translateY *= ratio;
        zoomLevel = nextZoom;
        updateZoom();
        return;
      }

      if (!isDragging) return;

      translateX = panStartX + (event.clientX - startX);
      translateY = panStartY + (event.clientY - startY);
      updateZoom();
    });

    const stopDrag = (event) => {
      pointers.delete(event.pointerId);

      try {
        if (surface.hasPointerCapture(event.pointerId)) {
          surface.releasePointerCapture(event.pointerId);
        }
      } catch (error) {
        /* Capture war nie gesetzt */
      }

      if (isPinching && pointers.size >= 2) {
        startPinch();
        return;
      }

      isPinching = false;
      isDragging = false;
      surface.classList.remove('is-dragging');

      // Vom Pinch auf einen Finger: nur mit der Maus als Pan weiterfuehren,
      // ein einzelner Finger scrollt wieder die Seite.
      if (pointers.size === 1 && event.pointerType !== 'touch') {
        const [{ clientX, clientY }] = Array.from(pointers.values());
        beginDrag(clientX, clientY);
      }
    };

    surface.addEventListener('pointerup', stopDrag);
    surface.addEventListener('pointercancel', stopDrag);
    surface.addEventListener('pointerleave', stopDrag);

    new ResizeObserver(updateZoom).observe(surface);

    updateZoom();
    zoomToPoint(initial);
  };

  const initAll = () => document.querySelectorAll('[data-weltkarte]').forEach(initWeltkarte);

  initAll();
  // Der Theme-Editor tauscht Sections im laufenden Betrieb aus.
  document.addEventListener('shopify:section:load', initAll);
})();
